import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  FileTextOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  TeamOutlined,
  InboxOutlined,
  SafetyOutlined,
  BarChartOutlined,
  SettingOutlined,
  FileProtectOutlined,
} from '@ant-design/icons';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/purchase-order');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="home-container">
      {/* Navigation Bar */}
      <nav className="home-navbar">
        <div className="nav-content">
          <div className="logo-section">
            <h1 className="company-name">Kalapi Prints</h1>
            <span className="tagline">Purchase Order Management</span>
          </div>
          <div className="nav-actions">
            {isAuthenticated ? (
              <button
                className="nav-button primary-button"
                onClick={() => navigate('/purchase-order')}
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  className="nav-button secondary-button"
                  onClick={() => navigate('/signup')}
                >
                  Sign Up
                </button>
                <button
                  className="nav-button primary-button"
                  onClick={() => navigate('/login')}
                >
                  Login
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Streamline Your Purchase Orders
          </h1>
          <p className="hero-description">
            Complete purchase order management solution for printing businesses.
            Manage vendors, products, orders, and track everything in one place.
          </p>
          <button className="cta-button" onClick={handleGetStarted}>
            Get Started
          </button>
        </div>
        <div className="hero-visual">
          <div className="visual-card">
            <div className="visual-icon"><FileTextOutlined /></div>
            <p>Purchase Orders</p>
          </div>
          <div className="visual-card">
            <div className="visual-icon"><InboxOutlined /></div>
            <p>Product Management</p>
          </div>
          <div className="visual-card">
            <div className="visual-icon"><ShopOutlined /></div>
            <p>Vendor Management</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <h2 className="section-title">Powerful Features</h2>
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon"><FileProtectOutlined /></div>
            <h3>Purchase Order Management</h3>
            <p>Create, track, and manage purchase orders with ease. Generate professional PDF documents instantly.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><TeamOutlined /></div>
            <h3>Vendor Management</h3>
            <p>Maintain comprehensive vendor database with contact information and transaction history.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><InboxOutlined /></div>
            <h3>Product Catalog</h3>
            <p>Organize your product inventory with categories, pricing, and specifications.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><SafetyOutlined /></div>
            <h3>Role-Based Access</h3>
            <p>Secure user authentication with granular permission controls and audit logs.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><BarChartOutlined /></div>
            <h3>Audit Logs</h3>
            <p>Track all system activities with comprehensive audit trails for accountability.</p>
          </div>

          <div className="feature-card">
            <div className="feature-icon"><SettingOutlined /></div>
            <h3>Configurable Settings</h3>
            <p>Customize delivery addresses, firm names, and categories to match your business needs.</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="benefits-content">
          <h2 className="section-title">Why Choose Kalapi Prints?</h2>
          <div className="benefits-list">
            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <div className="benefit-text">
                <h4>Simplified Workflow</h4>
                <p>Reduce manual paperwork and streamline your purchase order process</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <div className="benefit-text">
                <h4>Centralized Data</h4>
                <p>All your vendors, products, and orders in one secure location</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <div className="benefit-text">
                <h4>Professional Output</h4>
                <p>Generate polished PDF purchase orders ready for distribution</p>
              </div>
            </div>

            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <div className="benefit-text">
                <h4>Secure & Reliable</h4>
                <p>Enterprise-grade security with regular backups and audit trails</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to Get Started?</h2>
          <p>Join us today and transform your purchase order management</p>
          <button className="cta-button large" onClick={handleGetStarted}>
            {isAuthenticated ? 'Go to Dashboard' : 'Start Now'}
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>Kalapi Prints</h3>
            <p>Professional Purchase Order Management System</p>
          </div>
          <div className="footer-section">
            <p>&copy; {new Date().getFullYear()} Kalapi Prints. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
